import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiHeart } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import icon from "../../../assets/icon.svg";
import icon1 from "../../../assets/icon1.svg";
import squareMeters from "../../../assets/SquareMeters.svg";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { CurrencySymbol } from "../../../utils/enums";

export default function RecentSoldPropertySlider({
  property,
  isFavorite = false,
  onFavoriteClick,
  isAuthenticate,
  setAuthPopUp,
}) {
  const navigate = useNavigate();

  const p = property || {};
  const images = p?.frontPageImg || [];
  const mainImage = images[0];

  const formatSoldBadgeDate = (dateValue) => {
    if (!dateValue) return "";
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const soldBadgeText =
    p?.soldDate ||
      p?.sold_date ||
      p?.sold_date_time ||
      p?.saleDate ||
      p?.createdAt
      ? (() => {
        if (typeof p?.soldDate === "string" && p.soldDate.trim()) return `Sold on ${p.soldDate}`;
        if (typeof p?.sold_date === "string" && p.sold_date.trim())
          return `Sold on ${formatSoldBadgeDate(p.sold_date) || p.sold_date}`;
        if (typeof p?.saleDate === "string" && p.saleDate.trim())
          return `Sold on ${formatSoldBadgeDate(p.saleDate) || p.saleDate}`;
        if (p?.createdAt) return `Sold on ${formatSoldBadgeDate(p.createdAt)}`;
        return "Sold";
      })()
      : "";

  const handleCardClick = () => {
    navigate(`/property-house/${p?._id || p?.id}`);
  };

  const handleFavorite = (e) => {
    e.stopPropagation();
    if (!isAuthenticate && setAuthPopUp) {
      setAuthPopUp(true);
    } else if (onFavoriteClick) {
      onFavoriteClick(p);
    }
  };

  const priceText =
    p?.price && p?.price_display_checked === "show_actual_price"
      ? `${CurrencySymbol.NGN} ` + String(p.price).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : p?.price_display || null;

  const propertyTitle =
    p?.listingTitle ||
    p?.title ||
    p?.street_address_name ||
    `${p?.street_address_number || ""} ${p?.street_address_name || ""}`.trim() ||
    "Property";

  const fullAddress = [
    p?.street_address_number,
    p?.street_address_name,
    p?.suburb,
    p?.municipality,
  ]
    .filter(Boolean)
    .join(", ");

  const areaText = p?.type?.startsWith("House") ? p?.houseSize : p?.land_size;

  const primaryColor = p?.primary_color;

  return (
    <div
      className="bg-white  rounded-2xl overflow-hidden border-[1.5px] border-[#EAEAEA] transition-all duration-300 cursor-pointer"
      onClick={handleCardClick}
    >
      {/* 🔹 Property Image */}
      <div className="relative w-full">
        {/* Main Image */}
        {mainImage ? (
          <img
            src={mainImage}
            alt="property"
            className="w-full h-56 object-cover"
          />
        ) : (
          <div className="w-full h-56 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
            No image
          </div>
        )}

        {/* 50px Top Header Overlay */}
        <div
          className={`absolute top-0 left-0 w-full h-12  flex items-center px-4 py-1 ${primaryColor ? "" : "bg-black"}`}
          style={primaryColor ? { backgroundColor: primaryColor } : undefined}
        >
          <div className="flex items-center min-w-0">
            {p?.agencyLogo ? (
              <LazyLoadImage
                src={p?.agencyLogo}
                alt=""
                className="h-8 w-auto py-1 object-contain"
                effect="blur"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/agency-profile/${p?.agencyId}`);
                }}
              />
            ) : (
              <span className="text-white font-medium text-sm truncate" onClick={(e) => {
                e.stopPropagation();
                navigate(`/agency-profile/${p?.agencyId}`);
              }}>
                {p?.agencyName || "Agency"}
              </span>
            )}
          </div>
        </div>

        {soldBadgeText ? (
          <div className="absolute bottom-3 left-3 z-10 pointer-events-none bg-white px-3 py-2 rounded-lg border border-[#EAEAEA] shadow-sm flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#2B74F0]" />
            <span className="text-xs font-normal text-[#000929] whitespace-nowrap">
              {soldBadgeText}
            </span>
          </div>
        ) : null}

        {/* Profile Logo (Overlapping Header + Image) */}
        {p?.lead_agent?.profileImg && (
          <div className="absolute right-4 top-[18px]">
            <LazyLoadImage
              src={p.lead_agent.profileImg}
              alt=""
              className="h-[70px] w-[70px] rounded-full object-cover border-4 border-white shadow"
              effect="blur"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/agent-profile/${p?.lead_agent?.id}`);
              }}
            />
          </div>
        )}
      </div>

      {/* 🔹 Card Body */}
      <div className="p-4 flex flex-col ">
        <div className="flex items-center justify-between ">
          <div className="flex flex-col gap-1">
            {/* Price */}
            <h3 className="text-blue-600 text-2xl font-bold">{priceText}</h3>

            {/* Title */}
            <h2 className="text-xl font-semibold text-gray-800 mt-1 truncate w-60">
              {propertyTitle}
            </h2>
          </div>
          {/* Heart Icon */}
          <div
            className="p-3 rounded-full border-[1.5px] border-[#EAEAEA]"
            onClick={handleFavorite}
          >
            {isAuthenticate && p?.collections?.length > 0 ? (
              <FaHeart color="#097FE6" size={20} />
            ) : (
              <FiHeart color="#097FE6" size={20} />
            )}
          </div>
        </div>
        {/* Address */}
        <div className="flex items-center gap-2 text-gray-500 text-base mt-3">
          <p className="line-clamp-1">{fullAddress}</p>
        </div>
        <div>
          {/* Divider */}
          <div className="border-t my-3"></div>

          {/* Features */}
          <div className="flex items-center justify-between gap-3 text-[#000929] text-xs xl:text-sm font-medium">
            {p?.Bathrooms != null && (
              <span className="flex items-center gap-2">
                <img src={icon1} alt="icon" className="w-4 h-4 shrink-0" />
                {p.Bathrooms} Beds
              </span>
            )}
            {p?.Bedrooms != null && (
              <span className="flex items-center gap-2">
                <img src={icon} alt="icon" className="w-4 h-4 shrink-0" />
                {p.Bedrooms} Bathrooms
              </span>
            )}
            {areaText && (
              <span className="flex items-center gap-2">
                <img
                  src={squareMeters}
                  alt="icon"
                  className="w-4 h-4 shrink-0"
                />
                {areaText} m²
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
